import type {
  AgentStepReplyOutput,
  AgentStepStatus,
  MessageGenerationStep,
} from "@/features/ai-agent/types/agent-step";

/**
 * Statuses a step can never leave - the run is finished with it.
 *
 * Everything else can still move, and not always forwards: the approval gate
 * parks a `running` step back to `waiting`, and `approved` precedes the
 * execution that settles it. `deferred` is excluded for the same reason - the
 * send service flips it `deferred`→`executed` once the reply is delivered.
 */
const SETTLED_STATUSES: ReadonlySet<AgentStepStatus> = new Set([
  "resolved",
  "executed",
  "failed",
  "denied",
  "superseded",
]);

/** Whether this step has reached a status nothing will move it off. */
export const isSettledStep = (step: MessageGenerationStep): boolean =>
  SETTLED_STATUSES.has(step.status);

/** The timeline key a step files under - matches the event's correlationId. */
export const agentStepKeyOf = (
  step: Pick<MessageGenerationStep, "messageId" | "generationId">,
): string | null => step.messageId ?? step.generationId;

/** The backend renamed its step types and still sends the old names on the
 *  wire; both are accepted until every client reads the new ones. Typed on
 *  the one field they read, so a report's step row can use them too. */
export const isToolStep = (step: { type: string }): boolean =>
  step.type === "tool" || step.type === "execute_tool";

export const isReplyStep = (step: { type: string }): boolean =>
  step.type === "reply" || step.type === "response";

/** Ended without a reply to the customer: a stop, or an answer to the
 *  teammate who asked. */
export const isNoReplyStep = (step: { type: string }): boolean =>
  step.type === "stop" || step.type === "answer";

export const isApprovalStep = (step: MessageGenerationStep): boolean =>
  isToolStep(step) && Boolean(step.meta?.requiresApproval);

export const isPendingApproval = (step: MessageGenerationStep): boolean =>
  isApprovalStep(step) && step.status === "waiting";

/** Echo asking the teammate a question: answered, never approved. */
export const isQuestionStep = (step: MessageGenerationStep): boolean =>
  step.input?.type === "tool" && step.input.toolId === "ask_teammate";

export const isPendingResponse = (step: MessageGenerationStep): boolean =>
  isReplyStep(step) && step.status === "waiting";

/** The newest pending draft in an oldest-first step list, or null. A re-run files
 *  a second `waiting` response under the same correlation key before the first
 *  is superseded, and only the last one is live. */
export const findLastPendingResponse = (
  steps: MessageGenerationStep[],
): MessageGenerationStep | null => {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (isPendingResponse(steps[i])) return steps[i];
  }

  return null;
};

/** A tool step that mutates the world (vs a read-only query). */
export const isActionStep = (step: MessageGenerationStep): boolean =>
  Boolean(step.meta?.actionType || step.meta?.requiresApproval);

export const asReplyOutput = (
  step: MessageGenerationStep,
): AgentStepReplyOutput | null => {
  if (!isReplyStep(step) || !step.output) return null;

  return step.output as AgentStepReplyOutput;
};

/** Oldest first - createdAt asc with the (time-ordered UUIDv7) id as a
 *  tiebreak, matching the backend read order. */
export const sortAgentSteps = (
  steps: MessageGenerationStep[],
): MessageGenerationStep[] =>
  [...steps].sort((a, b) => {
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;

    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

export const upsertAgentStep = (
  steps: MessageGenerationStep[] | undefined,
  step: MessageGenerationStep,
): MessageGenerationStep[] => {
  const next = steps ? steps.filter((s) => s.id !== step.id) : [];

  next.push(step);

  return sortAgentSteps(next);
};

/** Whether the run currently needs a human (approval or draft review) or is
 *  still moving - used to auto-expand the timeline. */
export const hasLiveAttention = (steps: MessageGenerationStep[]): boolean =>
  steps.some(
    (step) =>
      step.status === "running" ||
      (step.status === "waiting" &&
        (isApprovalStep(step) || isReplyStep(step))),
  );
